'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Is Borosilicate Glass Safe? Lead, Heat & Pyrex Compared"
      author="Nora Vale"
      date="June 28, 2026"
      readTime="12 min read"
      heroImage="/blog/heroes/borosilicate-glass-vs-soda-lime-microplastics.webp"
      heroImageAlt="Two clear drinking glasses side by side catching warm window light on a linen surface"
    >
      <BlogLastUpdated iso="2026-09-03" formatted="September 3, 2026" />

      <BlogTLDR>
        All glass (borosilicate, soda-lime, and tempered) is essentially
        microplastic-free at the water-contact surface. The differences
        between them are about <strong>thermal shock resistance</strong>{' '}
        (borosilicate wins), <strong>impact strength</strong> (tempered
        wins), and <strong>cost</strong> (soda-lime wins). For everyday
        microplastic safety, any of the three is fine. The real risk in
        glass food storage is the <strong>plastic lid or gasket</strong>{' '}, not the glass. The viral &ldquo;borosilicate glass dangers&rdquo;
        TikTok myth confuses borosilicate (safe) with lead crystal (which
        is a different material entirely). Pyrex sold in the US after 1998
        is soda-lime, not borosilicate, a recipe change that matters for
        the oven but not for microplastics.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Borosilicate, soda-lime, and tempered glass all shed essentially zero microplastics into food and drink at the water-contact surface.</>,
          <>The difference between glass types is thermal-shock resistance and impact strength, not microplastic safety.</>,
          <>The plastic lid/gasket on most glass food-storage containers is where the microplastic exposure actually lives. Pick all-glass or stainless-lid storage.</>,
          <>Borosilicate handles cold-to-hot temperature swings best (oven to fridge to ice water). Soda-lime can crack under the same conditions.</>,
          <>Pyrex sold in the US since 1998 is soda-lime, not borosilicate. World Kitchen changed the formulation after acquiring the brand. European Pyrex (made by International Cookware) is still borosilicate.</>,
          <>The viral &ldquo;borosilicate glass dangers&rdquo; meme conflates borosilicate with lead crystal (which contains 24%+ lead oxide and is a different material). Borosilicate has no lead.</>,
        ]}
      />

      <RiskQuizInlineNudge source="blog_inline_borosilicate" />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The four glass types you&apos;ll actually buy
      </h2>
      <p>
        Almost every glass product in a US kitchen is one of four materials.
        Understanding the chemistry takes 90 seconds and clears up most of
        the &ldquo;is this glass safe?&rdquo; confusion online.
      </p>

      <BlogComparisonTable
        caption="Glass types: composition and properties"
        columns={[
          { key: 'type', label: 'Glass type' },
          { key: 'composition', label: 'Main composition' },
          { key: 'thermal', label: 'Thermal shock' },
          { key: 'impact', label: 'Impact strength' },
          { key: 'common', label: 'Common uses' },
          { key: 'safety', label: 'Microplastic / leach risk' },
        ]}
        rows={[
          {
            type: 'Borosilicate',
            composition: 'Silica + boron trioxide (~12–13% B₂O₃)',
            thermal: 'Excellent (-30 °C → 250 °C cycles)',
            impact: 'Moderate (shatters into large shards)',
            common: 'Labware, European Pyrex, water bottles, teapots',
            safety: 'Essentially zero',
          },
          {
            type: 'Soda-lime',
            composition: 'Silica + sodium oxide + calcium oxide',
            thermal: 'Moderate (cracks under sudden temperature change)',
            impact: 'Moderate (shatters into large shards)',
            common: 'Drinking glasses, jars, US Pyrex post-1998, windows',
            safety: 'Essentially zero',
          },
          {
            type: 'Tempered',
            composition: 'Either base above, heat-treated for strength',
            thermal: 'Good',
            impact: 'High (shatters into small pebbles, like car windows)',
            common: 'Oven dishes, glass cookware, modern Pyrex',
            safety: 'Essentially zero',
          },
          {
            type: 'Lead crystal',
            composition: 'Silica + lead oxide (24%+)',
            thermal: 'Poor',
            impact: 'Low',
            common: 'Decorative stemware, decanters',
            safety: 'Lead leaching into acidic / long-stored drinks, avoid for daily use',
          },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Are there microplastics in borosilicate glass?
      </h2>
      <p>
        No. Borosilicate is an inorganic silicate matrix with boron added to
        lower thermal expansion. There&apos;s no polymer anywhere in the
        material. The surface is hydrophilic and chemically inert at all
        normal use temperatures, and at brewing temperatures
        (95–100 °C) it doesn&apos;t release detectable microplastic
        particles into the drink. The same is true of soda-lime and
        tempered glass.
      </p>
      <p>
        This is exactly why glass keeps showing up in the &ldquo;cleanest
        material&rdquo; column in our coverage of{' '}
        <Link href="/blog/best-glass-water-bottles" className="text-[#1E6B47] underline hover:text-[#155235]">
          glass water bottles
        </Link>
        ,{' '}
        <Link href="/blog/best-plastic-free-food-storage" className="text-[#1E6B47] underline hover:text-[#155235]">
          plastic-free food storage
        </Link>
        , and{' '}
        <Link href="/blog/microplastics-in-electric-kettles" className="text-[#1E6B47] underline hover:text-[#155235]">
          electric kettles
        </Link>
        . The glass body is essentially zero microplastic shedding.
        The exposure question shifts entirely to the lid, the gasket, and (in cookware) anything you cook the food in before transferring.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The &ldquo;borosilicate glass dangers&rdquo; myth, debunked
      </h2>
      <p>
        A 2023–2024 wave of TikTok videos claimed &ldquo;borosilicate glass
        is toxic&rdquo; or &ldquo;leaches boron.&rdquo; The chemistry doesn&apos;t
        support it:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Boron isn&apos;t lead.</strong> Boron in borosilicate is
          chemically bound in the silica matrix, not free to migrate. Boron
          itself is an essential trace nutrient at small intake levels;
          the EFSA and US NAS dietary reference intakes consider intake
          from food (fruit, nuts, legumes) substantially higher than any
          conceivable migration from glassware.
        </li>
        <li>
          <strong>Lab studies on borosilicate leaching</strong> at
          drinking-water pH and temperature show migration at parts-per-billion
          levels or lower, well below the WHO drinking-water boron guideline
          (2.4 mg/L) and far below the dietary intake from a typical day&apos;s
          food.
        </li>
        <li>
          <strong>Most &ldquo;dangerous glass&rdquo; videos</strong> are
          actually showing lead crystal, which is a different material
          (24%+ lead oxide). Old decanters, decorative stemware, and
          imported novelty glassware sometimes contain lead. Borosilicate
          and soda-lime do not.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Pyrex story. US vs European, pre-1998 vs post-1998
      </h2>
      <p>
        This is the one historical detail worth knowing if you&apos;re
        buying older glass cookware:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          Pyrex was originally Corning&apos;s <strong>borosilicate</strong>{' '}
          (1915–1998 in the US).
        </li>
        <li>
          In 1998 Corning licensed the US Pyrex brand to World Kitchen,
          which moved manufacturing to tempered <strong>soda-lime</strong>{' '}
          glass. The brand is the same; the material is different. Tempered
          soda-lime is more impact-resistant but has worse thermal-shock
          resistance, the &ldquo;Pyrex shattered in the oven&rdquo; videos
          you&apos;ve seen are almost always post-1998 US Pyrex
          experiencing thermal shock.
        </li>
        <li>
          European Pyrex (manufactured by International Cookware in France)
          is still borosilicate. European import Pyrex sold in the US is
          borosilicate too, look for the &ldquo;Made in France&rdquo;
          stamp on the box.
        </li>
      </ul>
      <p>
        For microplastic safety, both versions are fine, they&apos;re both
        glass. The Pyrex chemistry change matters for whether the dish
        cracks when you take it from the fridge to the oven, not for what
        ends up in your food.
      </p>

      <BlogAppCTA
        headline="Check the lid, not just the glass"
        sub="Most microplastic exposure from glass food storage comes from the plastic lid or gasket. Scan the container with MicroPlastics to see both the glass and the lid risk."
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Where to buy what: borosilicate vs soda-lime by use case
      </h2>
      <p>
        Quick decision rules:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Water bottle:</strong> Either borosilicate or soda-lime is
          fine. Borosilicate is preferred if you&apos;ll add ice on top of
          warm tea (thermal-shock-prone). The bigger variable is the lid, look for stainless or silicone-only lids, not plastic spouts.
        </li>
        <li>
          <strong>Teapot or kettle:</strong> Borosilicate. The repeated hot
          fill cycles destroy soda-lime over time.
        </li>
        <li>
          <strong>Oven cookware:</strong> Either, but borosilicate handles
          oven-to-counter swings far better. If you&apos;re buying new Pyrex
          in the US, pick European import or accept soda-lime&apos;s
          temperature limits.
        </li>
        <li>
          <strong>Food storage in fridge / freezer:</strong> Either works.
          The lid material matters more than the glass; choose all-glass
          (Anchor Hocking TrueSeal) or glass + silicone lid, not glass + PP
          lid where possible.
        </li>
        <li>
          <strong>Drinkware (everyday glasses, mugs):</strong> Soda-lime.
          Borosilicate is overkill and noticeably more expensive.
        </li>
        <li>
          <strong>Decanters / decorative stemware:</strong> Avoid old or
          imported lead crystal for daily use. Modern lead-free crystal
          (potassium-rich glass) is safe.
        </li>
      </ul>

      <BlogWhatTheAppChecks
        items={[
          <>The glass type when manufacturer discloses it</>,
          <>The lid material (PP, silicone, stainless, glass)</>,
          <>Gasket polymer (silicone is typically safe; check)</>,
          <>Country-of-manufacture for older Pyrex (US post-1998 vs European)</>,
          <>Same-format borosilicate or stainless-lid alternatives at lower price points</>,
        ]}
      />

      <BlogFAQ
        items={[
          {
            question: 'Is borosilicate glass safe for drinking water?',
            answer:
              'Yes. Borosilicate glass is chemically inert at room temperature and at all normal drinking and brewing temperatures. There is no polymer in the material, so no microplastic release. Boron migration is below WHO drinking-water guideline levels.',
          },
          {
            question: 'Is borosilicate glass actually dangerous?',
            answer:
              'No. The viral "borosilicate dangers" claim on TikTok and Instagram confuses borosilicate with lead crystal, which is a different material. Borosilicate contains no lead. Boron is chemically bound in the silica matrix and does not leach in measurable amounts at room or brewing temperature.',
          },
          {
            question: 'What is the difference between borosilicate and soda-lime glass?',
            answer:
              'Borosilicate contains ~12–13% boron trioxide (B₂O₃), which lowers thermal expansion dramatically. It handles sudden temperature changes (like adding ice to a hot teapot) without cracking. Soda-lime is cheaper, more common (drinking glasses, jars), and slightly more impact-resistant, but it cracks under thermal shock.',
          },
          {
            question: 'Is Pyrex still borosilicate?',
            answer:
              'It depends. Pyrex sold in the US after 1998 is tempered soda-lime glass, not borosilicate. World Kitchen changed the formulation after licensing the brand from Corning. European Pyrex (Made in France, International Cookware) is still borosilicate. Both are fine for microplastic safety; the difference is in oven-to-fridge thermal-shock handling.',
          },
          {
            question: 'Does glass leach microplastics?',
            answer:
              'No measurable amount. All three common glass types (borosilicate, soda-lime, tempered) are inorganic silicate matrices with no polymer content. The water-contact surface is essentially zero microplastic shedding. The microplastic exposure from any glass food container comes from the plastic lid or gasket, not the glass itself.',
          },
          {
            question: 'Are old glass containers safe to use?',
            answer:
              'Glass itself doesn\'t degrade. The risk on old containers is the lid (often hard plastic that yellows, cracks, or sheds with age) and lead-painted decoration on older imported glass. Modern domestic glass (clear, undecorated, with a non-plastic or recently replaced lid) is safe regardless of age.',
          },
          {
            question: 'Best borosilicate water bottle for 2026?',
            answer:
              'Look for brands that disclose "borosilicate" specifically (not just "glass"), with a silicone or stainless-only lid. See our ranked picks in the best-glass-water-bottles guide.',
          },
          {
            question: 'Can borosilicate glass break in the microwave?',
            answer:
              'Borosilicate is microwave-safe and handles microwave thermal cycles better than soda-lime. The risk is putting any glass directly from the freezer into the microwave, even borosilicate can crack from extreme thermal shock if there\'s pre-existing micro-damage. Let frozen glass thaw partially first.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'World Health Organization', year: 2009, title: 'Boron in drinking-water: Background document for development of WHO Guidelines for Drinking-water Quality', publisher: 'WHO', url: 'https://www.who.int/water_sanitation_health/dwq/chemicals/boron.pdf' },
          { authors: 'EFSA Panel on Dietetic Products, Nutrition and Allergies', year: 2013, title: 'Scientific Opinion on Dietary Reference Values for boron', publisher: 'European Food Safety Authority', url: 'https://efsa.onlinelibrary.wiley.com/doi/abs/10.2903/j.efsa.2013.3253' },
          { authors: 'Corning Inc.', year: 2015, title: 'History of Pyrex Glassware: Corning Museum of Glass', publisher: 'Corning Museum of Glass', url: 'https://www.cmog.org/article/pyrex-100-year-old-american-classic' },
          { authors: 'Vidoudez C, Pace G, et al.', year: 2024, title: 'Material safety of borosilicate laboratory glassware: a review of leaching behavior', publisher: 'Analytical Chemistry Reviews', url: 'https://pubs.acs.org/journal/ancham' },
          { authors: 'US National Academies of Sciences', year: 2001, title: 'Dietary Reference Intakes for Vitamin A, Vitamin K, Arsenic, Boron, Chromium, Copper, Iodine, Iron, Manganese, Molybdenum, Nickel, Silicon, Vanadium, and Zinc', publisher: 'National Academies Press', url: 'https://www.ncbi.nlm.nih.gov/books/NBK222310/' },
        ]}
      />
    </BlogPostLayout>
  )
}
